






import * as warteschlange from "./warteschlange.js";
import { uid } from "../kern/format.js";


/* ═══════════════════════════════════════════════════════════════
   Etikettenfotos
   Je Jahrgang höchstens ein Foto. Vor dem Upload wird im Browser
   verkleinert und als JPEG neu kodiert — das spart Speicher und
   Bandbreite (der Server skaliert bewusst nicht selbst) und
   entfernt nebenbei die EXIF-Daten samt GPS.
   ═══════════════════════════════════════════════════════════════ */

const FOTO_MAX_KANTE = 1600;   /* lange Kante des Vollbilds */
const FOTO_THUMB_KANTE = 400;  /* Vorschau für die Weinseite */
const FOTO_QUALITAET = 0.82;



const FOTO_ROH_MAX = 15 * 1024 * 1024; /* Rohdatei vor dem Verkleinern */

/* Bild laden — createImageBitmap dreht nach EXIF-Orientierung. */
async function bildLaden(datei) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(datei, { imageOrientation: "from-image" }); }
    catch (e) { /* Fallback unten */ }
  }
  return await new Promise((ok, weg) => {
    const url = URL.createObjectURL(datei);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); ok(img); };
    img.onerror = () => { URL.revokeObjectURL(url); weg(new Error("Das Bildformat wird von diesem Browser nicht unterstützt.")); };
    img.src = url;
  });
}


function aufKanteZeichnen(bild, maxKante) {
  const bq = bild.width, hq = bild.height;
  const faktor = Math.min(1, maxKante / Math.max(bq, hq));
  const breite = Math.max(1, Math.round(bq * faktor));
  const hoehe = Math.max(1, Math.round(hq * faktor));
  const leinwand = document.createElement("canvas");
  leinwand.width = breite; leinwand.height = hoehe;
  const ctx = leinwand.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bild, 0, 0, breite, hoehe);
  return { leinwand, breite, hoehe };
}


const leinwandZuBase64 = (leinwand, qualitaet) => {
  const url = leinwand.toDataURL("image/jpeg", qualitaet);
  return url.slice(url.indexOf(",") + 1);
};


/* Datei → { voll, thumb, breite, hoehe } als Base64 (ohne Präfix). */
async function fotoVorbereiten(datei) {
  if (!/^image\//.test(datei.type || "")) throw new Error("Bitte eine Bilddatei wählen (JPEG, PNG, WebP).");
  if (datei.size > FOTO_ROH_MAX) throw new Error("Die Datei ist sehr groß (über 15 MB) — bitte ein kleineres Bild wählen.");
  const bild = await bildLaden(datei);
  const voll = aufKanteZeichnen(bild, FOTO_MAX_KANTE);
  const klein = aufKanteZeichnen(bild, FOTO_THUMB_KANTE);
  if (bild.close) bild.close();
  return {
    voll: leinwandZuBase64(voll.leinwand, FOTO_QUALITAET),
    thumb: leinwandZuBase64(klein.leinwand, 0.75),
    breite: voll.breite, hoehe: voll.hoehe,
  };
}


/* Ein vorbereitetes Bild zum Server schicken. Wirft bei HTTP-Fehlern
   mit der Meldung des Servers, bei Netzfehlern mit dem rohen
   TypeError — den unterscheidet der Aufrufer an fehlendem `.status`. */
async function bildSenden({ jgId, voll, thumb, breite, hoehe }) {
  const r = await fetch("/api/foto", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ jgId, voll, thumb, breite, hoehe }),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) {
    const fehler = new Error(json.fehler || "Der Upload ist fehlgeschlagen.");
    fehler.status = r.status;
    throw fehler;
  }
  return json.foto;
}


/** Etikett aufnehmen. Ist der Server erreichbar, liegt das Bild
    danach dort. Ist er es nicht, wandert es in die Warteschlange und
    wird trotzdem sofort angezeigt — mit einem Platzhalterpfad
    `wartend:<id>`, den `fotoWarteschlangeSenden()` später gegen den
    echten tauscht. Ein Foto wegzuwerfen, weil gerade kein Netz da
    ist, wäre die schlechteste aller Antworten: Die Flasche steht
    dann nicht mehr vor der Kamera. */
async function fotoHochladen(jgId, datei) {
  const vorbereitet = await fotoVorbereiten(datei);
  return vorbereitetHochladen({ jgId, ...vorbereitet });
}


/* Gemeinsamer Weg für Etikett und Rundum-Etikett: senden, oder bei
   fehlendem Netz in die Warteschlange. */
async function vorbereitetHochladen(daten) {
  try {
    return await bildSenden(daten);
  } catch (e) {
    /* Antwort vom Server (zu groß, abgelehnt) → durchreichen. Nur
       ein echter Netzfehler rechtfertigt die Warteschlange. */
    if (e && e.status) throw e;
    const id = uid();
    await warteschlange.einreihen({ id, angelegt: new Date().toISOString(), ...daten });
    return {
      pfad: wartePfad(id), thumb: wartePfad(id),
      breite: daten.breite || null, hoehe: daten.hoehe || null,
      bytes: null, hinzugefuegt: new Date().toISOString(), wartet: true,
    };
  }
}


/* ── Rundum-Etikett ────────────────────────────────────────────
   Die Abwicklung liegt bereits als Leinwand vor (kern/abwicklung.js),
   höchstens 2048 × 1024 Pixel. Sie geht als JPEG über denselben Weg
   wie ein Etikett — gleiche Route, gleiche Pfadregeln, gleiche
   Warteschlange ohne Netz. Die kleine Fassung (512 px lange Kante)
   ist die, die für den Offline-Betrieb vorgeladen wird; die
   3D-Ansicht zeigt sie, bis die große da ist. */
const RUNDUM_THUMB_KANTE = 512;

async function rundumHochladen(jgId, leinwand) {
  const klein = aufKanteZeichnen(leinwand, RUNDUM_THUMB_KANTE);
  return vorbereitetHochladen({
    jgId,
    voll: leinwandZuBase64(leinwand, 0.86),
    thumb: leinwandZuBase64(klein.leinwand, 0.8),
    breite: leinwand.width, hoehe: leinwand.height,
  });
}


/** Alle Bildpfade, die an einem Jahrgang hängen: Etikett (eigenes
    oder aus dem Katalog) und Rundum-Etikett. */
function jahrgangBildPfade(jg) {
  if (!jg) return [];
  const pfade = [];
  const bild = jg.foto || jg.katalogFoto;
  if (bild) pfade.push(bild.pfad, bild.thumb);
  if (jg.rundum) pfade.push(jg.rundum.pfad, jg.rundum.thumb);
  return pfade.filter(Boolean);
}


/** Wartende Bilder übertragen. Liefert je erledigtem Eintrag den
    Platzhalterpfad und das echte Foto, damit der Aufrufer die
    Verweise im Kellerbuch austauschen kann. */
async function fotoWarteschlangeSenden() {
  let offen;
  try { offen = await warteschlange.alle(); } catch (e) { return []; }
  const erledigt = [];
  for (const eintrag of offen) {
    try {
      const foto = await bildSenden(eintrag);
      await warteschlange.entfernen(eintrag.id);
      const alt = wartePfad(eintrag.id);
      const url = blobUrls.get(eintrag.id);
      if (url) blobUrls.delete(eintrag.id);
      erledigt.push({ alt, foto });
    } catch (e) {
      /* Netz wieder weg → später erneut. Eine Absage des Servers
         (zu groß, ungültig) würde ewig wiederkehren, deshalb fliegt
         der Eintrag dann raus. */
      if (e && e.status) { await warteschlange.entfernen(eintrag.id); continue; }
      break;
    }
  }
  return erledigt;
}


const fotoWarteschlangeAnzahl = () => warteschlange.anzahl();


async function fotosLoeschen(pfade) {
  const liste = (pfade || []).filter(Boolean);
  if (liste.length === 0) return;
  /* Wartende Bilder sind nie beim Server angekommen — sie werden
     einfach aus der Warteschlange genommen. */
  const wartend = liste.filter(istWartend);
  const echte = liste.filter((p) => !istWartend(p));
  for (const p of new Set(wartend.map(warteId))) {
    try { await warteschlange.entfernen(p); } catch (e) { /* egal */ }
    const url = blobUrls.get(p);
    if (url) blobUrls.delete(p);
  }
  if (echte.length === 0) return;
  try {
    await fetch("/api/foto/loeschen", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pfade: echte }),
    });
  } catch (e) { /* Verwaiste Dateien räumt später der Aufräumlauf ab */ }
}




/* ── Anzeige-URLs ──────────────────────────────────────────────

   Die URL eines Etiketts ist rein rechnerisch:
   `/api/foto/datei/<pfad>`, und der Pfad ist unveränderlich. Es
   braucht also keinen Serverabruf — und genau das macht die Bilder
   ohne Netz überhaupt erreichbar: Der Dienst-Arbeiter kann sein
   zwischengespeichertes Bild unter genau dieser URL anbieten.

   Bis Oktober 2026 gab es daneben die Supabase-Rückseite mit
   kurzlebigen signierten URLs, abgefragt über `POST /api/foto/urls`
   und im localStorage gemerkt. Beides ist entfernt; die alten
   Schlüssel werden beim Laden einmal weggeräumt. */

const WARTET = "wartend:";                 /* Präfix für Bilder in der Warteschlange */
const ALTE_SCHLUESSEL = ["winelake:fotourls:v1", "winelake:fotomodus:v1"];

const blobUrls = new Map();                /* Warte-ID → Objekt-URL */

const festeUrl = (pfad) => "/api/foto/datei/" + pfad.split("/").map(encodeURIComponent).join("/");

const istWartend = (pfad) => typeof pfad === "string" && pfad.startsWith(WARTET);
const warteId = (pfad) => (istWartend(pfad) ? pfad.slice(WARTET.length) : null);
const wartePfad = (id) => WARTET + id;


function alteSchluesselEntfernen() {
  try { for (const k of ALTE_SCHLUESSEL) localStorage.removeItem(k); }
  catch (e) { /* privater Modus, kein localStorage (Tests) */ }
}
alteSchluesselEntfernen();


/* Beim Abmelden: Bilder und wartende Uploads verwerfen. Auf einem
   geteilten Gerät soll vom abgemeldeten Keller nichts zurückbleiben. */
function fotoUrlsVergessen() {
  for (const url of blobUrls.values()) { try { URL.revokeObjectURL(url); } catch (e) {} }
  blobUrls.clear();
  try {
    if (typeof caches !== "undefined") caches.delete("winelake-bilder-v1").catch(() => {});
  } catch (e) {}
  warteschlange.leeren();
}


async function fotoUrlHolen(pfad) {
  if (!pfad) return null;

  /* Noch nicht übertragen: das Bild liegt in der Warteschlange und
     wird direkt von dort angezeigt. */
  if (istWartend(pfad)) {
    const id = warteId(pfad);
    if (blobUrls.has(id)) return blobUrls.get(id);
    const eintrag = await warteschlange.holen(id);
    if (!eintrag) throw new Error("Das Foto wurde nicht gefunden.");
    const url = "data:image/jpeg;base64," + (eintrag.thumb || eintrag.voll);
    blobUrls.set(id, url);
    return url;
  }

  return festeUrl(pfad);
}


/* Alle im Keller referenzierten Fotopfade (für den Aufräumlauf). */
function alleFotoPfade(db) {
  const pfade = [];
  for (const w of db.weine) for (const jg of w.jahrgaenge) {
    if (jg.foto) { pfade.push(jg.foto.pfad); if (jg.foto.thumb) pfade.push(jg.foto.thumb); }
    if (jg.rundum) { pfade.push(jg.rundum.pfad); if (jg.rundum.thumb) pfade.push(jg.rundum.thumb); }
  }
  /* Platzhalter wartender Uploads kennt der Server nicht. */
  return pfade.filter((p) => p && !istWartend(p));
}


/* Foto des jüngsten Jahrgangs eines Weins — Titelbild der Weinseite. */
function titelFoto(wein) {
  /* Ein aus dem Katalog übernommenes Etikett zählt genauso wie ein
     eigenes — für die Anzeige macht die Herkunft keinen Unterschied. */
  const mit = wein.jahrgaenge.filter((j) => j.foto || j.katalogFoto);
  if (mit.length === 0) return null;
  const sortiert = [...mit].sort((a, b) => (b.jahr || 0) - (a.jahr || 0));
  return sortiert[0];
}

/* Titel eines Weins als drehbare Flasche: das Rundum-Etikett des
   jüngsten Jahrgangs, der eines hat. Gibt es eines, steht es statt des
   Etikettfotos (gezeigt wird immer nur eines von beiden). */
function titelRundum(wein) {
  const mit = (wein.jahrgaenge || []).filter((j) => j.rundum && j.rundum.pfad);
  if (mit.length === 0) return null;
  return [...mit].sort((a, b) => (b.jahr || 0) - (a.jahr || 0))[0];
}

export { FOTO_MAX_KANTE, FOTO_QUALITAET, FOTO_ROH_MAX, FOTO_THUMB_KANTE, RUNDUM_THUMB_KANTE, alleFotoPfade, aufKanteZeichnen, bildLaden, festeUrl, fotoHochladen, jahrgangBildPfade, rundumHochladen, fotoUrlHolen, fotoUrlsVergessen, fotoWarteschlangeAnzahl, fotoWarteschlangeSenden, istWartend, fotoVorbereiten, fotosLoeschen, leinwandZuBase64, titelFoto, titelRundum };
